import { validateAddresses } from "./address";
import {
  FILING_COMPANY_NAME_MESSAGE,
  FILING_COMPANY_NAME_PATTERN,
} from "./company-name-characters";
import { RULE_CODES } from "./codes";
import { isValidFormationDate } from "./formation-date";
import { validateCCorpRoles, validateShareholders } from "./shareholders";
import { isValidEIN } from "./tax-id";
import type {
  CompanyType,
  ExistingCompanyInput,
  NewCompanyInput,
  RuleViolation,
} from "./types";
import { isSupportedFormationState, isValidUSState } from "./us-states";

export const DEFAULT_TOTAL_SHARES = 10_000_000;
export const DEFAULT_RESERVED_EQUITY = 0;

const MAX_COMPANY_NAME_LENGTH = 255;
const MAX_TOTAL_SHARES = 1_000_000_000;
const MAX_RESERVED_EQUITY = 99;

/**
 * Characters a company name may contain.
 *
 * This is an international product, so the letter classes are Unicode
 * properties rather than a Latin-1 range: `Łukasz Holdings` and
 * `Ünal Danışmanlık` are ordinary customer company names, and a range that
 * stops at `ÿ` turned them into a 400 on the final submit. `\p{Zs}` rather than
 * `\s` keeps tabs, newlines and other control whitespace out.
 *
 * The punctuation is the set real registered names carry — `Acme (US)`,
 * `Smith/Jones`, `A+B Capital`, `Ben & Jerry's`, `Yahoo!`, `E*TRADE`,
 * `100% Pure`, `Toys “R” Us`. Everything else is refused, which is what keeps
 * markup (`<`, `>`), braces, backticks, pipes, backslashes and control
 * characters out of a name that ends up on filings and in documents.
 *
 * Built with `new RegExp` rather than a literal because Product's tsconfig
 * still targets es5, under which tsc refuses the `u` flag on a regex literal
 * (TS1501). The flag is what enables the `\p{…}` escapes, and every runtime
 * this package ships to has supported them since ES2018.
 */
const COMPANY_NAME_PATTERN = new RegExp(
  String.raw`^[\p{L}\p{M}\p{N}\p{Zs}'’‘"“”.,:;&()/+*!?#%@_·–—-]+$`,
  "u",
);

/** Copy for {@link RULE_CODES.COMPANY_NAME_INVALID}, shared with the wizard's name steps. */
export const COMPANY_NAME_CHARACTERS_MESSAGE =
  "Company name can only contain letters, numbers, spaces and common punctuation such as ' . , & - ( ) / +";

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function companyTypeOf(value: unknown): CompanyType | null {
  const normalized = text(value)?.toLowerCase();
  return normalized === "llc" || normalized === "ccorp" ? normalized : null;
}

function validateName(
  name: string | null | undefined,
  pattern: RegExp,
  invalidMessage: string,
): RuleViolation[] {
  const value = text(name);

  if (!value) {
    return [
      {
        code: RULE_CODES.COMPANY_NAME_REQUIRED,
        field: "name",
        message: "Company name is required",
      },
    ];
  }

  if (value.length > MAX_COMPANY_NAME_LENGTH) {
    return [
      {
        code: RULE_CODES.COMPANY_NAME_TOO_LONG,
        field: "name",
        message: `Company name must be less than ${MAX_COMPANY_NAME_LENGTH} characters`,
      },
    ];
  }

  if (!pattern.test(value)) {
    return [
      {
        code: RULE_CODES.COMPANY_NAME_INVALID,
        field: "name",
        message: invalidMessage,
      },
    ];
  }

  return [];
}

/**
 * Does `name` use only the characters {@link COMPANY_NAME_PATTERN} allows?
 *
 * Exported for the wizard's two name steps, which have to reach the same
 * verdict the completion gate will: without it a rejected character is
 * invisible until the final submit answers 400 with no field marked. A blank
 * name passes here — that is the required rule's business, and the steps
 * already report it separately.
 */
export function hasAllowedCompanyNameCharacters(
  name: string | null | undefined,
): boolean {
  const value = text(name);
  return value === null || COMPANY_NAME_PATTERN.test(value);
}

/**
 * C-Corp share structure: the configured pool must be sane and the cap table
 * must fit inside it.
 */
function validateShareStructure(
  draft: NewCompanyInput,
  companyType: CompanyType,
): RuleViolation[] {
  if (companyType !== "ccorp") return [];

  const violations: RuleViolation[] = [];

  const totalShares = draft.totalShares ?? DEFAULT_TOTAL_SHARES;
  const reservedEquity = draft.reservedEquity ?? DEFAULT_RESERVED_EQUITY;

  const totalSharesValid =
    Number.isInteger(totalShares) &&
    totalShares > 0 &&
    totalShares <= MAX_TOTAL_SHARES;
  if (!totalSharesValid) {
    violations.push({
      code: RULE_CODES.TOTAL_SHARES_INVALID,
      field: "totalShares",
      message: `Total shares must be a whole number between 1 and ${MAX_TOTAL_SHARES}`,
    });
  }

  const reservedEquityValid =
    typeof reservedEquity === "number" &&
    !Number.isNaN(reservedEquity) &&
    reservedEquity >= 0 &&
    reservedEquity <= MAX_RESERVED_EQUITY;
  if (!reservedEquityValid) {
    violations.push({
      code: RULE_CODES.RESERVED_EQUITY_INVALID,
      field: "reservedEquity",
      message: `Reserved equity must be between 0% and ${MAX_RESERVED_EQUITY}%`,
    });
  }

  if (!totalSharesValid || !reservedEquityValid) {
    return violations;
  }

  const availableShares = Math.round(totalShares * (1 - reservedEquity / 100));
  const allocatedShares = (draft.shareholders ?? []).reduce(
    (sum, shareholder) =>
      sum + (shareholder.ownershipDetails?.numberOfShares ?? 0),
    0,
  );

  if (allocatedShares > availableShares) {
    violations.push({
      code: RULE_CODES.CCORP_SHARES_EXCEED_AVAILABLE,
      field: "shareholders",
      message: `Total allocated shares (${allocatedShares.toLocaleString("en-US")}) cannot exceed available shares (${availableShares.toLocaleString("en-US")})`,
    });
  }

  return violations;
}

/**
 * Full rulebook for a company being formed.
 *
 * `now` is accepted for signature symmetry with
 * {@link validateExistingCompany}; a new company has no formation date yet.
 */
export function validateNewCompany(
  draft: NewCompanyInput,
  now: Date,
): RuleViolation[] {
  void now;

  const violations: RuleViolation[] = [
    ...validateName(
      draft?.name,
      FILING_COMPANY_NAME_PATTERN,
      FILING_COMPANY_NAME_MESSAGE,
    ),
  ];

  const companyType = companyTypeOf(draft?.type);
  if (companyType === null) {
    violations.push({
      code: RULE_CODES.COMPANY_TYPE_INVALID,
      field: "type",
      message: "Company type must be LLC or CCorp",
    });
  }

  const formationState = text(draft?.formationState);
  if (!formationState) {
    violations.push({
      code: RULE_CODES.FORMATION_STATE_REQUIRED,
      field: "formationState",
      message: "Formation state is required",
    });
  } else if (!isSupportedFormationState(formationState)) {
    violations.push({
      code: RULE_CODES.FORMATION_STATE_UNSUPPORTED,
      field: "formationState",
      message: "Formation state must be Delaware or Wyoming",
    });
  }

  if (companyType !== null) {
    violations.push(...validateShareStructure(draft, companyType));
    violations.push(
      ...validateShareholders(draft?.shareholders ?? [], companyType),
    );
    if (companyType === "ccorp") {
      violations.push(...validateCCorpRoles(draft?.shareholders ?? []));
    }
  }

  violations.push(...validateAddresses(draft?.addresses ?? []));

  return violations;
}

/**
 * Full rulebook for a company that already exists. Formation state may be any
 * US state, an EIN and formation date are required, and the corporate-role
 * requirements are skipped because the board is already seated.
 */
export function validateExistingCompany(
  draft: ExistingCompanyInput,
  now: Date,
): RuleViolation[] {
  // Existing companies are already registered under their name; the broad
  // pattern stays so a real registered name is never refused.
  const violations: RuleViolation[] = [
    ...validateName(
      draft?.name,
      COMPANY_NAME_PATTERN,
      COMPANY_NAME_CHARACTERS_MESSAGE,
    ),
  ];

  const companyType = companyTypeOf(draft?.type);
  if (companyType === null) {
    violations.push({
      code: RULE_CODES.COMPANY_TYPE_INVALID,
      field: "type",
      message: "Company type must be LLC or CCorp",
    });
  }

  const formationState = text(draft?.formationState);
  if (!formationState) {
    violations.push({
      code: RULE_CODES.FORMATION_STATE_REQUIRED,
      field: "formationState",
      message: "Formation state is required",
    });
  } else if (!isValidUSState(formationState)) {
    violations.push({
      code: RULE_CODES.FORMATION_STATE_INVALID,
      field: "formationState",
      message: "Invalid US state code. Please select a valid state.",
    });
  }

  const formationDate = text(draft?.formationDate);
  if (!formationDate) {
    violations.push({
      code: RULE_CODES.FORMATION_DATE_REQUIRED,
      field: "formationDate",
      message: "Formation date is required",
    });
  } else if (!isValidFormationDate(formationDate, now)) {
    violations.push({
      code: RULE_CODES.FORMATION_DATE_INVALID,
      field: "formationDate",
      message:
        "Formation date must be a real date in YYYY-MM-DD format and cannot be in the future",
    });
  }

  const ein = text(draft?.ein);
  if (!ein) {
    violations.push({
      code: RULE_CODES.EIN_REQUIRED,
      field: "ein",
      message: "EIN is required",
    });
  } else if (!isValidEIN(ein)) {
    violations.push({
      code: RULE_CODES.EIN_INVALID,
      field: "ein",
      message: "EIN must be in format XX-XXXXXXX",
    });
  }

  if (companyType !== null) {
    violations.push(...validateShareStructure(draft, companyType));
    violations.push(
      ...validateShareholders(draft?.shareholders ?? [], companyType),
    );
  }

  violations.push(...validateAddresses(draft?.addresses ?? []));

  return violations;
}
